import { DomainError } from "./errors";
import { fiscalInstant } from "./nfe-events";

// ===== Notas de entrada (T2.5, RF-SUP-04): DistDFe, manifestação do destinatário e lançamento do XML =====

/** Espera depois de 137 (nenhum documento), NSU no máximo, 656 (consumo indevido) ou erro (NT 2014.002). */
export const DFE_WAIT_MS = 3_600_000;
/** Rodada que parou por teto de lotes com documentos sobrando: a próxima vem logo. */
export const DFE_CONTINUE_MS = 60_000;
/** Falha transitória do provedor (rede, 5xx): nova tentativa. */
export const DFE_RETRY_MS = 10 * 60_000;
/** Lotes (pedidos de distribuição) por rodada. */
export const DFE_MAX_BATCHES = 5;

export type DfeSyncReason = "continue" | "wait" | "blocked" | "error";

/** Próxima consulta permitida pelo resultado da rodada. */
export function nextDfeSync(
  o: { code: string | null; ultNsu: number; maxNsu: number; transient?: boolean },
  now: Date,
): { nextAt: string; reason: DfeSyncReason } {
  const after = (ms: number, reason: DfeSyncReason) => ({
    nextAt: new Date(now.getTime() + ms).toISOString(),
    reason,
  });
  if (o.transient) return after(DFE_RETRY_MS, "error");
  if (o.code === "656") return after(DFE_WAIT_MS, "blocked");
  if (o.code === "137") return after(DFE_WAIT_MS, "wait");
  if (o.code === "138")
    return o.ultNsu < o.maxNsu ? after(DFE_CONTINUE_MS, "continue") : after(DFE_WAIT_MS, "wait");
  return after(DFE_WAIT_MS, "error");
}

/** Pede o próximo lote na mesma rodada? */
export const dfeShouldContinue = (code: string | null, ultNsu: number, maxNsu: number, batches: number) =>
  code === "138" && ultNsu < maxNsu && batches < DFE_MAX_BATCHES;

// ---------- manifestação ----------

export const MANIFEST_KINDS = ["ciencia", "confirmacao", "desconhecimento", "nao_realizada"] as const;
export type ManifestKind = (typeof MANIFEST_KINDS)[number];
export const MANIFEST_EVENT_CODE: Readonly<Record<ManifestKind, string>> = {
  ciencia: "210210",
  confirmacao: "210200",
  desconhecimento: "210220",
  nao_realizada: "210240",
};
export const MANIFEST_LABEL: Readonly<Record<ManifestKind, string>> = {
  ciencia: "Ciência da operação",
  confirmacao: "Confirmação da operação",
  desconhecimento: "Desconhecimento da operação",
  nao_realizada: "Operação não realizada",
};
/** Prazo da manifestação desde a emissão (a confirmar com o contador; mesmo prazo para todos os eventos). */
export const MANIFEST_WINDOW_DAYS = 180;
export const isConclusiveManifest = (k: ManifestKind) => k !== "ciencia";

/** Motivo para recusar a manifestação (código + mensagem pt-BR) ou null. */
export function manifestProblem(
  i: {
    kind: ManifestKind;
    current: ManifestKind | null;
    pending: boolean;
    issuedAt: string | null;
    justification: string | null;
    imported: boolean;
  },
  now: Date,
): { code: string; message: string } | null {
  if (i.pending)
    return { code: "event_in_progress", message: "Já há uma manifestação em envio para esta nota." };
  if (i.current && isConclusiveManifest(i.current))
    return {
      code: "manifest_final",
      message: `A nota já tem manifestação conclusiva (${MANIFEST_LABEL[i.current]}).`,
    };
  if (i.current === i.kind)
    return { code: "manifest_duplicate", message: "Esta manifestação já foi registrada." };
  if (i.kind === "nao_realizada") {
    const j = i.justification?.trim() ?? "";
    if (j.length < 15 || j.length > 255)
      return {
        code: "manifest_justification",
        message: "Informe a justificativa da operação não realizada (15 a 255 caracteres).",
      };
  }
  if (i.imported && (i.kind === "desconhecimento" || i.kind === "nao_realizada"))
    return {
      code: "dfe_already_imported",
      message:
        "A entrada desta nota já foi lançada. Estorne a entrada antes de desconhecer ou recusar a operação.",
    };
  if (i.issuedAt) {
    const limit = fiscalInstant(i.issuedAt) + MANIFEST_WINDOW_DAYS * 86_400_000;
    if (Number.isFinite(limit) && now.getTime() > limit)
      return {
        code: "manifest_window_expired",
        message: `O prazo da manifestação (${MANIFEST_WINDOW_DAYS} dias da emissão) terminou.`,
      };
  }
  return null;
}

// ---------- vínculo dos itens ----------

/** Texto para comparação: sem acento, maiúsculo, só letras e dígitos separados por um espaço. */
export const normalizeItemText = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();

export type MatchSource = "supplier_code" | "gtin" | "ncm_description";
export type MatchIndex = {
  /** Código do produto no fornecedor (cProd) → produto e fator aprendido. */
  bySupplierCode: ReadonlyMap<string, { productId: string; factorQ4: number | null }>;
  /** GTIN → produtos ativos com ele (GTIN ou GTIN tributário). */
  byGtin: ReadonlyMap<string, readonly string[]>;
  /** `${ncm}|${descrição normalizada}` → produtos. */
  byNcmDescription: ReadonlyMap<string, readonly string[]>;
};
export type InboundItemKey = {
  code: string;
  description: string;
  ncm: string | null;
  gtin: string | null;
  gtinTax: string | null;
};
export const ncmDescriptionKey = (ncm: string, description: string) =>
  `${ncm}|${normalizeItemText(description)}`;

const realGtin = (g: string | null) => (g && /^\d{8,14}$/.test(g) ? g : null);
const only = (ids: readonly string[] | undefined) => (ids && ids.length === 1 ? ids[0]! : null);

/**
 * Vínculo determinístico (06 §3, sem IA): código do fornecedor aprendido > GTIN comercial > GTIN tributável > NCM +
 * descrição normalizada idêntica. Mais de um produto com a mesma chave = sem vínculo (fica para o usuário/IA).
 */
export function matchInboundItem(
  it: InboundItemKey,
  idx: MatchIndex,
): { productId: string; source: MatchSource; factorQ4: number | null; viaTaxGtin: boolean } | null {
  const sc = idx.bySupplierCode.get(it.code);
  if (sc)
    return { productId: sc.productId, source: "supplier_code", factorQ4: sc.factorQ4, viaTaxGtin: false };
  const g = realGtin(it.gtin);
  const byG = g ? only(idx.byGtin.get(g)) : null;
  if (byG) return { productId: byG, source: "gtin", factorQ4: null, viaTaxGtin: false };
  const gt = realGtin(it.gtinTax);
  const byGt = gt && gt !== g ? only(idx.byGtin.get(gt)) : null;
  if (byGt) return { productId: byGt, source: "gtin", factorQ4: null, viaTaxGtin: true };
  if (it.ncm) {
    const byN = only(idx.byNcmDescription.get(ncmDescriptionKey(it.ncm, it.description)));
    if (byN) return { productId: byN, source: "ncm_description", factorQ4: null, viaTaxGtin: false };
  }
  return null;
}

const unitOf = (u: string) => u.trim().toUpperCase();

/** Divisão inteira arredondada (meio para cima), positivos. */
const roundDiv = (num: bigint, den: bigint) => (num * 2n + den) / (2n * den);
const safe = (v: bigint, code: string) => {
  const n = Number(v);
  if (!Number.isSafeInteger(n)) throw new DomainError(code, `valor fora do limite: ${v}`);
  return n;
};

/**
 * Fator (q4) de unidades do produto por unidade comercial do XML: aprendido > unidade comercial igual (1) > unidade
 * tributável igual (qTrib/qCom) > 1 marcado "conferir".
 */
export function unitFactorQ4(
  i: { unit: string; qtyQ4: number; taxUnit: string | null; taxQtyQ4: number | null },
  productUnit: string,
  learnedQ4: number | null,
): { factorQ4: number; needsReview: boolean } {
  if (learnedQ4 && learnedQ4 > 0) return { factorQ4: learnedQ4, needsReview: false };
  const pu = unitOf(productUnit);
  if (unitOf(i.unit) === pu) return { factorQ4: 10_000, needsReview: false };
  if (i.taxUnit && unitOf(i.taxUnit) === pu && i.taxQtyQ4 && i.qtyQ4 > 0) {
    const f = safe(roundDiv(BigInt(i.taxQtyQ4) * 10_000n, BigInt(i.qtyQ4)), "invalid_qty");
    if (f > 0) return { factorQ4: f, needsReview: false };
  }
  return { factorQ4: 10_000, needsReview: true };
}

/** Quantidade de entrada no estoque (unidade do produto) = qCom × fator. */
export const entryQtyQ4 = (qtyQ4: number, factorQ4: number) =>
  safe(roundDiv(BigInt(qtyQ4) * BigInt(factorQ4), 10_000n), "invalid_qty");

/** Custo do item na entrada (centavos): valor − desconto + frete + seguro + outras + IPI + ST + FCP-ST. */
export function entryItemCost(v: {
  totalCents: number;
  discountCents: number;
  freightCents: number;
  insuranceCents: number;
  otherCents: number;
  ipiCents: number;
  stCents: number;
  fcpStCents: number;
}): number {
  return (
    v.totalCents -
    v.discountCents +
    v.freightCents +
    v.insuranceCents +
    v.otherCents +
    v.ipiCents +
    v.stCents +
    v.fcpStCents
  );
}

/** Custo unitário (centavos por unidade do produto) para o custo médio. */
export const entryUnitCost = (costCents: number, qtyQ4: number) =>
  qtyQ4 <= 0 || costCents <= 0
    ? 0
    : safe(roundDiv(BigInt(costCents) * 10_000n, BigInt(qtyQ4)), "invalid_money");

/** Parcelas da conta a pagar: as duplicatas (cobr/dup) com valor, ou uma na data de entrada com o total. */
export function entryInstallments(
  dups: readonly { dueDate: string; amountCents: number }[],
  totalCents: number,
  entryDate: string,
): { dueDate: string; amountCents: number }[] {
  const valid = dups.filter((d) => d.amountCents > 0);
  if (valid.length) return valid.map((d) => ({ dueDate: d.dueDate, amountCents: d.amountCents }));
  return totalCents > 0 ? [{ dueDate: entryDate, amountCents: totalCents }] : [];
}

// ---------- XML ----------

/** Número do XML da NF-e ("1234.50", ponto decimal, sem milhar) em inteiro escalado; casas a mais arredondam. */
export function parseXmlDecimal(raw: string, scale: number): number {
  const s = raw.trim();
  const m = /^(-?)(\d{1,15})(?:\.(\d{1,10}))?$/.exec(s);
  if (!m) throw new DomainError("invalid_xml_number", `número inválido no XML: ${raw.slice(0, 30)}`);
  const frac = m[3] ?? "";
  const digits = BigInt(m[2]! + frac.padEnd(scale, "0").slice(0, Math.max(scale, frac.length)));
  const extra = Math.max(0, frac.length - scale);
  let v = extra ? roundDiv(digits, 10n ** BigInt(extra)) : digits;
  if (m[1] === "-") v = -v;
  return safe(v, "invalid_xml_number");
}

/** CNPJ (ou CPF com zeros) do emitente nas posições 7–20 da chave de acesso. */
export const accessKeyCnpj = (key: string) => key.slice(6, 20);
/** Modelo do documento (posições 21–22). */
export const accessKeyModel = (key: string) => key.slice(20, 22);
